// Regexp to match various 'token' variants
const tokenRegExp = /(token=)[^&]+/g; /* global, to handle multiple matches */
// eslint-disable-next-line spaced-comment
const emailRegExp = /([a-zA-Z0-9._+-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi; //NOSONAR email is from page URL
/**
 * Helper function to remove sensitive hash/query parameter tokens from url (ie, `id_token=...`, `access_token=...`)
 * @param url string to sanitize
 */
export const sanitizeUrl = (url) => {
    const sanitizedUrl = url
        .replace(tokenRegExp, '$1' + '<token>')
        .replace(emailRegExp, '<email>');
    const odataIndex = url.toLowerCase().indexOf('/odata/');
    if (odataIndex > -1) {
        return sanitizedUrl.substring(0, odataIndex) + '/odata/<odata>';
    }
    return sanitizedUrl;
};
/**
 * Helper function to remove sensitive email addresses from objects
 * @param obj object to sanitize
 * @returns sanitized object or error object with generic error to prevent leaking sensitive data
 */
export const sanitizeEmailFromObject = (obj) => {
    try {
        const objectJson = JSON.stringify(obj);
        const sanitizedJson = objectJson.replace(emailRegExp, '<email>');
        return JSON.parse(sanitizedJson);
    }
    catch (e) {
        // eslint-disable-next-line no-console
        console.error('request', `Error sanitizing object: ${e.message}`);
        return { error: `Error sanitizing object` };
    }
};
function getHeaders(token) {
    return {
        'Content-Type': 'application/json; charset=UTF-8',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
    };
}
function getBody(body) {
    return body ? JSON.stringify(body) : '';
}
function defaultErrorCallback(error, method, url, requestProps) {
    // eslint-disable-next-line no-console
    console.error('request', `Error making ${method} request to ${url}: ${error.message} with props ${JSON.stringify(requestProps)}`);
    throw error;
}
async function defaultSuccessCallback(response) {
    const contentType = response.headers.get('content-type');
    let body = await response.text();
    if ((contentType === null || contentType === void 0 ? void 0 : contentType.indexOf('application/json')) !== -1) {
        try {
            body = JSON.parse(body);
        }
        catch (_a) {
            // eslint-disable-next-line no-console
            console.warn('request', 'Response body is not JSON but content-type header is application/json');
        }
    }
    if (response.ok) {
        return body;
    }
    throw body;
}
export async function safeFetch(method, url, requestProps, errorCallback = defaultErrorCallback, successCallback = defaultSuccessCallback) {
    try {
        const hasBody = method === 'POST' || method === 'PUT' || method === 'PATCH';
        const fetchProps = Object.assign({ method, headers: Object.assign(Object.assign({}, getHeaders(requestProps.token)), requestProps.headers) }, (hasBody ? { body: getBody(requestProps.body) } : {}));
        // The default value for credentials is "same-origin",
        // we want to override it only if we have a custom value defined
        // in the requestProps.credentials.
        if (requestProps.credentials != null) {
            fetchProps.credentials = requestProps.credentials;
        }
        const res = await fetch(url, fetchProps);
        return await successCallback(res);
    }
    catch (e) {
        return errorCallback(e, method, sanitizeUrl(url), Object.assign(Object.assign({}, sanitizeEmailFromObject(requestProps)), { token: requestProps.token ? '<token>' : 'none' }));
    }
}
export async function get(url, requestProps, errorCallback = defaultErrorCallback, successCallback = defaultSuccessCallback) {
    return safeFetch('GET', url, requestProps, errorCallback, successCallback);
}
export async function put(url, requestProps, errorCallback = defaultErrorCallback, successCallback = defaultSuccessCallback) {
    return safeFetch('PUT', url, requestProps, errorCallback, successCallback);
}
export async function post(url, requestProps, errorCallback = defaultErrorCallback, successCallback = defaultSuccessCallback) {
    return safeFetch('POST', url, requestProps, errorCallback, successCallback);
}
export async function patch(url, requestProps, errorCallback = defaultErrorCallback, successCallback = defaultSuccessCallback) {
    return safeFetch('PATCH', url, requestProps, errorCallback, successCallback);
}
